import React, { useState } from "react";
import { Card } from "react-bootstrap";
import "./CollapsibleCard.css";

interface Props {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  subtitleClassName?: string;
  /** Shown in the header beside the title while the body is open. */
  actions?: React.ReactNode;
  /** When false the title is plain text and the body is always shown. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Controlled open state. Leave unset to let the card manage it. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  bodyId: string;
  className?: string;
  bodyClassName?: string;
  children?: React.ReactNode;
  /** Rendered inside the card after the body, whether open or not (modals and the like). */
  footer?: React.ReactNode;
}

const CollapsibleCard: React.FC<Props> = ({
  title,
  subtitle,
  subtitleClassName,
  actions,
  collapsible = true,
  defaultOpen = true,
  open,
  onToggle,
  bodyId,
  className,
  bodyClassName,
  children,
  footer,
}) => {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const isOpen = open ?? innerOpen;
  const bodyOpen = !collapsible || isOpen;

  const toggle = () => {
    const next = !isOpen;
    if (open === undefined) setInnerOpen(next);
    onToggle?.(next);
  };

  return (
    <Card className={`collapsible-card${className ? ` ${className}` : ""}`}>
      <Card.Header>
        <div className="collapsible-card__head">
          <div className="collapsible-card__block">
            {collapsible ? (
              <h2 className="h5 mb-1">
                <button
                  type="button"
                  className="collapsible-card__caret btn btn-link btn-sm"
                  aria-expanded={isOpen}
                  aria-controls={bodyId}
                  onClick={toggle}
                >
                  <span className="collapsible-card__mark" aria-hidden="true">{isOpen ? "\u25BE" : "\u25B8"}</span>
                  {title}
                </button>
              </h2>
            ) : (
              <h2 className="h5 mb-1">{title}</h2>
            )}
            {subtitle && (
              <p className={`collapsible-card__subtitle ${subtitleClassName ?? "text-muted small mb-0"}`}>{subtitle}</p>
            )}
          </div>
          {bodyOpen && actions}
        </div>
      </Card.Header>
      {bodyOpen && (
        <Card.Body id={bodyId} className={bodyClassName}>
          {children}
        </Card.Body>
      )}
      {footer}
    </Card>
  );
};

export default CollapsibleCard;
